import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { submitPrayer } from "@/lib/api";

export default function EnrollDialog({ trigger }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", experience: "beginner", message: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Please add your name and email."); return; }
    setLoading(true);
    try {
      const res = await submitPrayer(form);
      toast.success(res.message || "You're enrolled!");
      setOpen(false);
      setForm({ name: "", email: "", phone: "", experience: "beginner", message: "" });
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-md bg-cream border-plum/10 max-h-[90vh] overflow-y-auto" data-testid="enroll-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-3xl text-plum flex items-center gap-2"><BookOpen className="text-gold-dark" size={24} /> Prayer Class Enrollment</DialogTitle>
          <DialogDescription className="text-stone">Enrollment is open. Everyone is welcome &mdash; reserve your place below.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div><Label className="text-plum text-sm">Full name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="enroll-name" className="h-12 bg-white border-plum/15 mt-1" /></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-plum text-sm">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="enroll-email" className="h-12 bg-white border-plum/15 mt-1" /></div>
            <div><Label className="text-plum text-sm">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="enroll-phone" className="h-12 bg-white border-plum/15 mt-1" /></div>
          </div>
          <div>
            <Label className="text-plum text-sm">Experience with prayer</Label>
            <Select value={form.experience} onValueChange={(v) => setForm({ ...form, experience: v })}>
              <SelectTrigger className="h-12 bg-white border-plum/15 mt-1" data-testid="enroll-experience"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="beginner">New to prayer</SelectItem>
                <SelectItem value="growing">Growing in prayer</SelectItem>
                <SelectItem value="intercessor">Experienced intercessor</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-plum text-sm">Anything you'd like us to know? (optional)</Label><Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="enroll-message" className="bg-white border-plum/15 mt-1" rows={3} /></div>
          <button type="submit" disabled={loading} data-testid="enroll-submit-btn" className="w-full py-4 rounded-full bg-plum hover:bg-plum-dark text-cream font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
            {loading ? <Loader2 className="animate-spin" size={18} /> : <BookOpen size={18} />}{loading ? "Enrolling..." : "Reserve My Place"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
